import React, { useEffect, useState } from 'react'
import { supabase, parseRpcError } from '@/lib/supabaseClient'
import type { AllocationMode, CompanyAllocationRow, HotelRow, RoomTypeRow } from '@/lib/types'
import { Badge, Button, Card, EmptyState, Input, Modal, Select, Textarea, useConfirm, useToast, Field } from '@/components/ui'
import { allocationCapacity, nightsBetween } from '@/lib/domain/roomLogic'

interface Props {
  eventId: string
  companyId: string
  onGoToRoomingList: () => void
}

interface AllocationWithNames extends CompanyAllocationRow {
  hotel_name: string
  room_type_name: string
}

export default function AllocationEditor({ eventId, companyId, onGoToRoomingList }: Props) {
  const [hotels, setHotels] = useState<HotelRow[]>([])
  const [roomTypes, setRoomTypes] = useState<RoomTypeRow[]>([])
  const [allocations, setAllocations] = useState<AllocationWithNames[] | null>(null)
  const [editing, setEditing] = useState<AllocationWithNames | 'new' | null>(null)
  const { push } = useToast()
  const { confirm, confirmDialog } = useConfirm()

  async function load() {
    const [{ data: h }, { data: rt }, { data: allocs }] = await Promise.all([
      supabase.from('hotels').select('*').eq('event_id', eventId).eq('is_active', true).order('name'),
      supabase.from('room_types').select('*').eq('event_id', eventId).eq('is_active', true).order('name'),
      supabase.from('company_allocations').select('*, hotels(name), room_types(name)').eq('company_id', companyId).order('created_at'),
    ])
    setHotels((h as HotelRow[]) || [])
    setRoomTypes((rt as RoomTypeRow[]) || [])
    setAllocations(
      ((allocs as any[]) || []).map((a) => ({ ...a, hotel_name: a.hotels?.name || '?', room_type_name: a.room_types?.name || '?' }))
    )
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId])

  async function removeAllocation(a: AllocationWithNames) {
    if (a.room_count > 0) {
      push('Reduce Number of Rooms to 0 first (see below) before deleting an allocation row.', 'error')
      return
    }
    const ok = await confirm({ title: `Remove this allocation?`, confirmLabel: 'Remove', tone: 'danger' })
    if (!ok) return
    const { error } = await supabase.rpc('admin_delete_allocation', { p_allocation_id: a.id })
    if (error) {
      push(parseRpcError(error).code, 'error')
      return
    }
    push('Allocation removed')
    load()
  }

  if (allocations === null) return null

  const totalRooms = allocations.reduce((s, a) => s + a.room_count, 0)
  const totalCapacity = allocations.reduce((s, a) => s + allocationCapacity({ roomCount: a.room_count, capacityPerRoom: a.capacity_per_room }), 0)

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-slate-600">
          <span className="font-medium text-slate-900">{totalRooms}</span> room(s) ·{' '}
          <span className="font-medium text-slate-900">{totalCapacity}</span> guest capacity
        </div>
        <Button variant="primary" onClick={() => setEditing('new')} disabled={hotels.length === 0 || roomTypes.length === 0}>
          + Add Allocation
        </Button>
      </div>

      {(hotels.length === 0 || roomTypes.length === 0) && (
        <p className="mb-4 text-sm text-amber-700">Add at least one hotel and one room type to this event before allocating rooms.</p>
      )}

      {allocations.length === 0 ? (
        <EmptyState title="No room allocations yet" action={<Button variant="primary" onClick={() => setEditing('new')}>Add Allocation</Button>} />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Hotel</th>
                <th className="px-4 py-2.5">Room Type</th>
                <th className="px-4 py-2.5">Mode</th>
                <th className="px-4 py-2.5 text-right">Rooms</th>
                <th className="px-4 py-2.5 text-right">Capacity</th>
                <th className="px-4 py-2.5 text-right">Guest Capacity</th>
                <th className="px-4 py-2.5">Check-in</th>
                <th className="px-4 py-2.5">Check-out</th>
                <th className="px-4 py-2.5 text-right">Nights</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {allocations.map((a) => {
                const nights = nightsBetween(a.check_in, a.check_out)
                return (
                  <tr key={a.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5">{a.hotel_name}</td>
                    <td className="px-4 py-2.5">{a.room_type_name}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {a.allocation_mode === 'night_budget' ? (
                          <Badge tone="blue">Night budget: {a.night_budget ?? '-'}</Badge>
                        ) : (
                          <>
                            {a.unlimited_rooms && <Badge tone="blue">Unlimited rooms</Badge>}
                            {a.unlimited_capacity && <Badge tone="green">Unlimited guests/room</Badge>}
                            {!a.unlimited_rooms && !a.unlimited_capacity && <span className="text-xs text-slate-400">Fixed</span>}
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right">{a.allocation_mode === 'night_budget' ? '-' : a.room_count}</td>
                    <td className="px-4 py-2.5 text-right">{a.capacity_per_room}</td>
                    <td className="px-4 py-2.5 text-right">{a.allocation_mode === 'night_budget' ? '-' : allocationCapacity({ roomCount: a.room_count, capacityPerRoom: a.capacity_per_room })}</td>
                    <td className="px-4 py-2.5 text-slate-500">{a.check_in || '-'}</td>
                    <td className="px-4 py-2.5 text-slate-500">{a.check_out || '-'}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-slate-700">{nights ?? '-'}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" variant="secondary" onClick={() => setEditing(a)}>
                          Edit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => removeAllocation(a)}>
                          Remove
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
              <tr className="bg-slate-50 font-medium text-slate-700">
                <td className="px-4 py-2" colSpan={3}>
                  Total
                </td>
                <td className="px-4 py-2 text-right">{totalRooms}</td>
                <td />
                <td className="px-4 py-2 text-right">{totalCapacity}</td>
                <td colSpan={3} />
                <td />
              </tr>
            </tbody>
          </table>
        </Card>
      )}

      {editing && (
        <AllocationModal
          eventId={eventId}
          companyId={companyId}
          hotels={hotels}
          roomTypes={roomTypes}
          allocation={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            load()
          }}
          onGoToRoomingList={onGoToRoomingList}
        />
      )}
      {confirmDialog}
    </div>
  )
}

function AllocationModal({
  eventId,
  companyId,
  hotels,
  roomTypes,
  allocation,
  onClose,
  onSaved,
  onGoToRoomingList,
}: {
  eventId: string
  companyId: string
  hotels: HotelRow[]
  roomTypes: RoomTypeRow[]
  allocation: AllocationWithNames | null
  onClose: () => void
  onSaved: () => void
  onGoToRoomingList: () => void
}) {
  const [hotelId, setHotelId] = useState(allocation?.hotel_id || hotels[0]?.id || '')
  const [roomTypeId, setRoomTypeId] = useState(allocation?.room_type_id || roomTypes[0]?.id || '')
  const [roomCount, setRoomCount] = useState(allocation?.room_count ?? 1)
  const [capacity, setCapacity] = useState(allocation?.capacity_per_room ?? (roomTypes[0]?.default_capacity || 1))
  const [checkIn, setCheckIn] = useState(allocation?.check_in || '')
  const [checkOut, setCheckOut] = useState(allocation?.check_out || '')
  const [notes, setNotes] = useState(allocation?.notes || '')
  const [labelPrefix, setLabelPrefix] = useState(allocation?.label_prefix || '')
  const [mode, setMode] = useState<AllocationMode>(allocation?.allocation_mode || 'fixed')
  const [nightBudget, setNightBudget] = useState(allocation?.night_budget ?? 10)
  const [unlimitedRooms, setUnlimitedRooms] = useState(allocation?.unlimited_rooms ?? false)
  const [unlimitedCapacity, setUnlimitedCapacity] = useState(allocation?.unlimited_capacity ?? false)
  const [saving, setSaving] = useState(false)
  const [blockers, setBlockers] = useState<{ display_label: string; occupied_guest_count: number }[] | null>(null)
  const { push } = useToast()

  useEffect(() => {
    if (!allocation) {
      const rt = roomTypes.find((r) => r.id === roomTypeId)
      if (rt) setCapacity(rt.default_capacity)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomTypeId])

  async function submit() {
    if (!hotelId || !roomTypeId || capacity < 1) {
      push('Please fill in hotel, room type and a valid capacity', 'error')
      return
    }
    if (mode === 'night_budget' && (!nightBudget || nightBudget < 1)) {
      push('Please enter a valid total nights budget', 'error')
      return
    }
    if (mode === 'fixed' && roomCount < 0) {
      push('Please enter a valid room count', 'error')
      return
    }

    // If shrinking a fixed-mode allocation, preview blockers first so we
    // never surprise the admin with a raw DB error. Not applicable in
    // night_budget mode, where the admin isn't setting a room count at all.
    if (mode === 'fixed' && allocation && (roomCount < allocation.room_count || capacity < allocation.capacity_per_room)) {
      const { data } = await supabase.rpc('find_allocation_decrease_blockers', {
        p_allocation_id: allocation.id,
        p_new_room_count: roomCount,
        p_new_capacity_per_room: capacity,
      })
      if (data && data.length > 0) {
        setBlockers(data)
        return
      }
    }

    await doSave()
  }

  async function doSave() {
    setSaving(true)
    const { error } = await supabase.rpc('admin_upsert_allocation', {
      p_allocation_id: allocation?.id ?? null,
      p_event_id: eventId,
      p_company_id: companyId,
      p_hotel_id: hotelId,
      p_room_type_id: roomTypeId,
      p_room_count: mode === 'night_budget' ? 0 : roomCount,
      p_capacity_per_room: capacity,
      p_check_in: checkIn || null,
      p_check_out: checkOut || null,
      p_notes: notes || null,
      p_label_prefix: labelPrefix || null,
      p_allocation_mode: mode,
      p_night_budget: mode === 'night_budget' ? nightBudget : null,
      p_unlimited_rooms: mode === 'fixed' ? unlimitedRooms : false,
      p_unlimited_capacity: unlimitedCapacity,
    })
    setSaving(false)
    if (error) {
      const { code, detail } = parseRpcError(error)
      push(detail || code, 'error')
      return
    }
    push(allocation ? 'Allocation updated' : mode === 'night_budget' ? 'Allocation saved — the company can now add its own rooms' : `Allocation saved — ${roomCount} room slot(s) generated`)
    onSaved()
  }

  if (blockers) {
    return (
      <Modal open onClose={onClose} title="Rooms contain guest data">
        <p className="text-sm text-slate-700">
          {blockers.length} room(s) still have guests in them and can't be shrunk or removed automatically:
        </p>
        <ul className="my-3 max-h-40 space-y-1 overflow-y-auto rounded border border-slate-200 p-2 text-sm">
          {blockers.map((b) => (
            <li key={b.display_label} className="flex justify-between">
              <span>{b.display_label}</span>
              <span className="text-slate-500">{b.occupied_guest_count} guest(s)</span>
            </li>
          ))}
        </ul>
        <p className="text-sm text-slate-600">Move those guests to another room first, then try again.</p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setBlockers(null)}>
            Back
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              onClose()
              onGoToRoomingList()
            }}
          >
            Move Guests First
          </Button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={allocation ? 'Edit Allocation' : 'Add Allocation'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Hotel" required>
            <Select value={hotelId} onChange={(e) => setHotelId(e.target.value)}>
              {hotels.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Room Type" required>
            <Select value={roomTypeId} onChange={(e) => setRoomTypeId(e.target.value)}>
              {roomTypes.map((rt) => (
                <option key={rt.id} value={rt.id}>
                  {rt.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Allocation Mode" hint="Opt-in per allocation — leave as Fixed unless this company needs one of the self-service options below.">
          <Select value={mode} onChange={(e) => setMode(e.target.value as AllocationMode)}>
            <option value="fixed">Fixed — I set the room count</option>
            <option value="night_budget">Night Budget — company splits a total nights budget itself</option>
          </Select>
        </Field>

        {mode === 'fixed' ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Number of Rooms" required>
              <Input type="number" min={0} value={roomCount} onChange={(e) => setRoomCount(Math.max(0, parseInt(e.target.value) || 0))} />
            </Field>
            <Field label="Capacity per Room" required>
              <Input type="number" min={1} value={capacity} onChange={(e) => setCapacity(Math.max(1, parseInt(e.target.value) || 1))} />
            </Field>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Total Nights Budget" required hint="Total nights the company may split across the rooms it creates itself (e.g. 10 nights = 5 rooms x 2 nights, or 2 rooms x 5 nights).">
              <Input type="number" min={1} value={nightBudget} onChange={(e) => setNightBudget(Math.max(1, parseInt(e.target.value) || 1))} />
            </Field>
            <Field label="Capacity per Room" required hint="Guest capacity for each room the company creates.">
              <Input type="number" min={1} value={capacity} onChange={(e) => setCapacity(Math.max(1, parseInt(e.target.value) || 1))} />
            </Field>
          </div>
        )}

        {mode === 'fixed' && (
          <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" className="mt-0.5" checked={unlimitedRooms} onChange={(e) => setUnlimitedRooms(e.target.checked)} />
              <span>
                <strong>Unlimited rooms</strong> — let the company add extra rooms of this type itself, beyond the number above.
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" className="mt-0.5" checked={unlimitedCapacity} onChange={(e) => setUnlimitedCapacity(e.target.checked)} />
              <span>
                <strong>Unlimited guests per room</strong> — let the company add extra guests to a room beyond its set capacity.
              </span>
            </label>
          </div>
        )}
        {mode === 'night_budget' && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" className="mt-0.5" checked={unlimitedCapacity} onChange={(e) => setUnlimitedCapacity(e.target.checked)} />
              <span>
                <strong>Unlimited guests per room</strong> — let the company add extra guests to a room beyond its set capacity.
              </span>
            </label>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label={mode === 'night_budget' ? 'Default Check-in Date' : 'Check-in Date'}>
            <Input type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} />
          </Field>
          <Field label={mode === 'night_budget' ? 'Default Check-out Date' : 'Check-out Date'}>
            <Input type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} />
          </Field>
        </div>
        {mode === 'fixed' && nightsBetween(checkIn || null, checkOut || null) != null && (
          <p className="text-xs text-slate-500">
            <strong className="text-slate-700">{nightsBetween(checkIn || null, checkOut || null)}</strong> night
            {nightsBetween(checkIn || null, checkOut || null) === 1 ? '' : 's'} — applied to every room generated by this allocation (you can still
            override an individual room's dates afterwards from the Rooming List).
          </p>
        )}
        {mode === 'night_budget' && (
          <p className="text-xs text-slate-500">
            Each room the company adds picks its own check-in/check-out — these defaults are not currently pre-filled automatically.
          </p>
        )}
        <Field label="Custom Label Prefix" hint="Optional. Defaults to the room type name, e.g. SINGLE-001.">
          <Input value={labelPrefix} onChange={(e) => setLabelPrefix(e.target.value)} placeholder="e.g. RM" />
        </Field>
        <Field label="Notes">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        {mode === 'fixed' && (
          <p className="text-xs text-slate-500">
            Total guest capacity for this row: <strong>{(roomCount * capacity) || 0}</strong>
            {unlimitedRooms || unlimitedCapacity ? ' (plus whatever the company adds itself)' : ''}
          </p>
        )}
      </div>
    </Modal>
  )
}
